import {
  CheckCircleOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { Alert, Empty, Select, Skeleton, Table, Tag, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { apiService } from '../../services/api';
import { formatApiErrorDetail } from '../../utils/formatApiErrorDetail';
import { formatMskDateTime } from '../../utils/moscow-calendar-date';

const RECOVERY_PHASE_LABELS = {
  preflight: 'Предпроверка',
  modem_sync: 'Синхронизация модемов',
  invalidate_refs: 'Сброс устаревших связей',
  rebind_orphans: 'Пересадка потерянных прокси',
  push_users: 'Отправка пользователей',
  push_proxies: 'Отправка прокси',
  verify: 'Проверка',
  done: 'Готово',
};

const RECOVERY_STATUS_LABELS = {
  draft: 'Черновик',
  running: 'Выполняется',
  partially_completed: 'Частично завершен',
  completed: 'Завершен',
  failed: 'С ошибкой',
  cancelled: 'Отменен',
};

const TERMINAL_RUN_STATUSES = new Set(['completed', 'failed', 'partially_completed', 'cancelled']);

const RECOVERY_PHASE_ORDER = [
  'preflight',
  'modem_sync',
  'invalidate_refs',
  'rebind_orphans',
  'push_users',
  'push_proxies',
  'verify',
  'done',
];

const getPhaseIndex = (phaseKey) => RECOVERY_PHASE_ORDER.indexOf(phaseKey);

const getStageErrors = (stageKey, stageRaw) => {
  const defaultsErrors = stageKey === 'modem_sync'
    ? (stageRaw?.defaults_reset?.errors || [])
    : [];
  return [...(stageRaw?.errors || []), ...defaultsErrors];
};

const getModemDefaultsAppliedCount = (stageRaw) => (
  stageRaw?.defaults_applied_count
  ?? stageRaw?.defaults_reset?.reset_count
  ?? 0
);

const getModemSyncDoneLabel = (stageRaw) => {
  const appliedCount = getModemDefaultsAppliedCount(stageRaw);
  const totalCount = stageRaw?.defaults_reset?.total_count;
  const failedCount = stageRaw?.defaults_reset?.failed_count || 0;
  if (totalCount > 0 && failedCount > 0) {
    return `частично (${appliedCount}/${totalCount})`;
  }
  if (appliedCount > 0) {
    return `готово (${appliedCount})`;
  }
  return 'готово';
};

const formatRecoveryErrorMessage = (item) => {
  if (!item) {
    return 'Ошибка без текста';
  }
  if (item.message) {
    return item.message;
  }
  if (item.code === 'modem_defaults_reset_failed' || item.modem_name) {
    const params = (item.failed_parameters || []).join(', ') || '—';
    return (
      `Модем «${item.modem_name || `#${item.modem_id}`}» (kraken_id=${item.kraken_modem_id ?? '—'}): `
      + `не удалось передать [${params}] на шаге ${item.failed_step || 'update_modem'}`
    );
  }
  return item.code || 'Ошибка без текста';
};

const getStageStatusLabel = (stageKey, stageHasReport, run, stageRaw) => {
  if (run?.status === 'running' && run.phase === stageKey) {
    if (stageRaw?.in_progress) {
      const percent = stageRaw?.progress?.percent;
      return percent != null ? `выполняется ${percent}%` : 'выполняется';
    }
    return 'выполняется';
  }

  if (stageKey === 'done') {
    if (!run || !TERMINAL_RUN_STATUSES.has(run.status)) {
      return 'ожидает';
    }
    if (run.status === 'completed') {
      return 'готово';
    }
    if (run.status === 'partially_completed') {
      return 'частично';
    }
    if (run.status === 'failed') {
      return 'с ошибкой';
    }
    return 'отменено';
  }

  if (stageHasReport) {
    const stageErrors = getStageErrors(stageKey, stageRaw);
    if (stageRaw?.skipped) {
      if (stageErrors.length > 0) {
        return 'пропущено, с ошибками';
      }
      return 'пропущено';
    }
    const stageWarnings = stageRaw?.warnings || [];
    if (stageRaw?.success === false || stageErrors.length > 0) {
      return 'с ошибками';
    }
    if (stageKey === 'rebind_orphans') {
      const reboundCount = stageRaw?.rebound_count || 0;
      if (reboundCount > 0 && stageWarnings.length > 0) {
        return `пересажено ${reboundCount}, с предупреждениями`;
      }
      if (reboundCount > 0) {
        return `готово (${reboundCount})`;
      }
    }
    if (stageWarnings.length > 0) {
      return 'с предупреждениями';
    }
    if (stageKey === 'modem_sync') {
      return getModemSyncDoneLabel(stageRaw);
    }
    return 'готово';
  }

  if (!run) {
    return 'ожидает';
  }
  if (run.status === 'draft') {
    return 'запуск';
  }

  const currentPhaseIndex = getPhaseIndex(run.phase);
  const stageIndex = getPhaseIndex(stageKey);
  if (currentPhaseIndex > stageIndex) {
    return 'готово';
  }

  return 'ожидает';
};

const getStageClassName = (stage, run) => {
  const baseClass = 'conn-recovery-stage';
  const { key: stageKey, hasReport, raw: stageRaw } = stage;

  if (run?.status === 'running' && run.phase === stageKey) {
    return `${baseClass} conn-recovery-stage--running`;
  }

  if (stageKey === 'done' && run && TERMINAL_RUN_STATUSES.has(run.status)) {
    if (run.status === 'completed') {
      return `${baseClass} conn-recovery-stage--done`;
    }
    if (run.status === 'partially_completed') {
      return `${baseClass} conn-recovery-stage--warning`;
    }
    return `${baseClass} conn-recovery-stage--error`;
  }

  if (!hasReport) {
    const currentPhaseIndex = getPhaseIndex(run?.phase);
    const stageIndex = getPhaseIndex(stageKey);
    if (run && currentPhaseIndex > stageIndex) {
      return `${baseClass} conn-recovery-stage--done`;
    }
    return baseClass;
  }

  if (!stageRaw) {
    return `${baseClass} conn-recovery-stage--done`;
  }

  const stageErrors = getStageErrors(stageKey, stageRaw);
  if (stageRaw.skipped) {
    if (stageErrors.length > 0) {
      return `${baseClass} conn-recovery-stage--error`;
    }
    return `${baseClass} conn-recovery-stage--skipped`;
  }
  if (
    stageKey === 'modem_sync'
    && getModemDefaultsAppliedCount(stageRaw) > 0
    && (stageRaw?.defaults_reset?.failed_count || 0) > 0
  ) {
    return `${baseClass} conn-recovery-stage--warning`;
  }
  if (stageRaw.success === false || stageErrors.length > 0) {
    return `${baseClass} conn-recovery-stage--error`;
  }
  const stageWarnings = stageRaw.warnings || [];
  if (stageWarnings.length > 0) {
    return `${baseClass} conn-recovery-stage--warning`;
  }
  return `${baseClass} conn-recovery-stage--done`;
};

const formatDateTime = formatMskDateTime;

const renderStatusTag = (status) => {
  const color = status === 'completed'
    ? 'success'
    : status === 'running'
      ? 'processing'
      : status === 'partially_completed'
        ? 'warning'
        : status === 'failed'
          ? 'error'
          : 'default';
  return <Tag color={color}>{RECOVERY_STATUS_LABELS[status] || status}</Tag>;
};

const ConnectionsRecovery = () => {
  const [connections, setConnections] = useState([]);
  const [selectedConnectionIds, setSelectedConnectionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runsLoading, setRunsLoading] = useState(true);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const [retryLoadingRunId, setRetryLoadingRunId] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [runs, setRuns] = useState([]);
  const [activeRunId, setActiveRunId] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const loadConnections = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getKrakenConnections();
      const nextConnections = Array.isArray(response?.data?.connections)
        ? response.data.connections
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setConnections(nextConnections);
      setSelectedConnectionIds((previousIds) => {
        if (previousIds.length > 0) {
          return previousIds.filter((id) => nextConnections.some((connection) => connection.id === id));
        }
        return nextConnections
          .filter((connection) => Boolean(connection?.is_active))
          .map((connection) => connection.id);
      });
    } catch (_error) {
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const response = await apiService.listKrakenRecoveryRuns();
      const nextRuns = Array.isArray(response?.data?.runs) ? response.data.runs : [];
      const latestActiveRun = nextRuns.find((run) => run.status === 'running' || run.status === 'draft');
      setRuns(nextRuns);
      setActiveRunId((previousRunId) => previousRunId || latestActiveRun?.id || null);
      setActiveRun((previousRun) => {
        if (previousRun) {
          return nextRuns.find((run) => run.id === previousRun.id) || previousRun;
        }
        return latestActiveRun || previousRun;
      });
    } catch (_error) {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  const loadStatus = useCallback(async (runId) => {
    if (!runId) {
      return null;
    }
    const response = await apiService.getKrakenRecoveryStatus(runId);
    const run = response?.data?.run || null;
    if (run) {
      setActiveRun(run);
    }
    return run;
  }, []);

  useEffect(() => {
    loadConnections();
    loadRuns();
  }, [loadConnections, loadRuns]);

  useEffect(() => {
    if (!activeRunId) {
      return undefined;
    }
    if (activeRun && TERMINAL_RUN_STATUSES.has(activeRun.status)) {
      return undefined;
    }

    const intervalId = window.setInterval(async () => {
      try {
        const nextRun = await loadStatus(activeRunId);
        if (nextRun && TERMINAL_RUN_STATUSES.has(nextRun.status)) {
          await loadRuns();
        }
      } catch (_error) {
        // Оставляем polling живым до следующей попытки
      }
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [activeRun, activeRunId, loadRuns, loadStatus]);

  const activeConnections = useMemo(
    () => connections.filter((connection) => Boolean(connection?.is_active)),
    [connections]
  );

  const inactiveConnections = Math.max(0, connections.length - activeConnections.length);

  const selectedPayload = useMemo(
    () => ({
      connection_ids: selectedConnectionIds.length > 0 ? selectedConnectionIds : undefined,
    }),
    [selectedConnectionIds]
  );

  const handleRunPreflight = useCallback(async () => {
    setPreflightLoading(true);
    try {
      const response = await apiService.getKrakenRecoveryPreflight(selectedPayload);
      const nextPreflight = response?.data?.preflight || null;
      setPreflight(nextPreflight);
    } catch (error) {
      message.error(
        formatApiErrorDetail(error)
          || error?.response?.data?.detail
          || error?.message
          || 'Не удалось выполнить предпроверку'
      );
    } finally {
      setPreflightLoading(false);
    }
  }, [selectedPayload]);

  const handleStartRecovery = useCallback(async () => {
    setStartLoading(true);
    try {
      const response = await apiService.startKrakenRecovery({
        ...selectedPayload,
        reset_stale_ids: true,
        sync_modems_first: true,
        verify_after_run: true,
        reset_modem_defaults: true,
        full_kraken_verify: true,
        parallel_proxy_push: true,
      });
      const run = response?.data?.run || null;
      if (run) {
        setActiveRun(run);
        setActiveRunId(run.id);
        await loadRuns();
        message.success(`Сценарий восстановления #${run.id} запущен`);
      }
    } catch (error) {
      message.error(error?.response?.data?.detail || error?.message || 'Не удалось запустить восстановление');
    } finally {
      setStartLoading(false);
    }
  }, [loadRuns, selectedPayload]);

  const handleRetryRun = useCallback(async (runId) => {
    setRetryLoadingRunId(runId);
    try {
      const response = await apiService.retryKrakenRecoveryFailed(runId);
      const run = response?.data?.run || null;
      if (run) {
        setActiveRun(run);
        setActiveRunId(run.id);
        await loadRuns();
        message.success(`Сценарий восстановления #${run.id} перезапущен`);
      }
    } catch (error) {
      message.error(error?.response?.data?.detail || error?.message || 'Не удалось перезапустить восстановление');
    } finally {
      setRetryLoadingRunId(null);
    }
  }, [loadRuns]);

  const stageRows = useMemo(() => {
    const report = activeRun?.report || {};
    return Object.keys(RECOVERY_PHASE_LABELS).map((phaseKey) => ({
      key: phaseKey,
      phase: RECOVERY_PHASE_LABELS[phaseKey],
      hasReport: Boolean(report[phaseKey]) || (
        activeRun?.status === 'running' && activeRun?.phase === phaseKey
      ),
      raw: report[phaseKey],
    }));
  }, [activeRun]);

  const modemDefaultsResetSummary = useMemo(() => {
    const resetReport = activeRun?.report?.modem_sync?.defaults_reset;
    if (!resetReport?.total_count) {
      return null;
    }
    return resetReport.summary
      || `Настройки модемов: обновлено ${resetReport.reset_count || 0} из ${resetReport.total_count}`;
  }, [activeRun]);

  const orphanRebindSummary = useMemo(() => {
    const rebindReport = activeRun?.report?.rebind_orphans;
    if (!rebindReport || rebindReport.orphaned_count == null) {
      return null;
    }
    return (
      `Потерянные прокси: пересажено ${rebindReport.rebound_count || 0}`
      + ` из ${rebindReport.orphaned_count}`
    );
  }, [activeRun]);

  const modemDefaultsResetErrors = useMemo(() => {
    const resetErrors = activeRun?.report?.modem_sync?.defaults_reset?.errors || [];
    return resetErrors.map((item, index) => ({
      key: `modem-reset-error-${index}`,
      message: formatRecoveryErrorMessage(item),
    }));
  }, [activeRun]);

  const generalRunErrors = useMemo(() => (
    (activeRun?.errors || [])
      .filter((item) => item?.code !== 'modem_defaults_reset_failed')
      .map((item, index) => ({
        key: `run-error-${index}`,
        message: formatRecoveryErrorMessage(item),
      }))
  ), [activeRun]);

  const activeRunWarnings = useMemo(() => {
    const report = activeRun?.report || {};
    return Object.keys(report).flatMap((phaseKey) => {
      const phaseReport = report[phaseKey];
      const warnings = phaseReport?.warnings || [];
      if (!warnings.length) {
        return [];
      }
      return warnings.map((item, index) => ({
        key: `${phaseKey}-warning-${index}`,
        phase: RECOVERY_PHASE_LABELS[phaseKey] || phaseKey,
        message: item.message || item.code || 'Предупреждение без текста',
      }));
    });
  }, [activeRun]);

  const runsColumns = useMemo(() => [
    {
      title: 'Запуск',
      dataIndex: 'id',
      key: 'id',
      width: 84,
      render: (value) => `#${value}`,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (value) => renderStatusTag(value),
    },
    {
      title: 'Фаза',
      dataIndex: 'phase',
      key: 'phase',
      width: 180,
      render: (value) => RECOVERY_PHASE_LABELS[value] || value || '—',
    },
    {
      title: 'Подключения',
      key: 'connections',
      width: 140,
      render: (_, record) => (record.connection_ids || []).length,
    },
    {
      title: 'Старт',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 190,
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Завершение',
      dataIndex: 'finished_at',
      key: 'finished_at',
      width: 190,
      render: (value) => formatDateTime(value),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <button
          type="button"
          className={`conn-header__action-btn conn-header__action-btn--primary${retryLoadingRunId === record.id ? ' conn-header__action-btn--loading' : ''}`}
          style={{ width: 'auto', padding: '0 12px' }}
          onClick={() => handleRetryRun(record.id)}
          disabled={
            retryLoadingRunId === record.id ||
            !['failed', 'partially_completed', 'completed'].includes(record.status)
          }
        >
          Повтор
        </button>
      ),
    },
  ], [handleRetryRun, retryLoadingRunId]);

  return (
    <div className="conn-recovery-page">
      <div className="conn-recovery-summary">
        <div className="conn-recovery-summary__card">
          <span className="conn-recovery-summary__icon">
            <SafetyOutlined />
          </span>
          <div>
            <div className="conn-recovery-summary__value">{connections.length}</div>
            <div className="conn-recovery-summary__label">Подключений в контуре восстановления</div>
          </div>
        </div>
        <div className="conn-recovery-summary__card">
          <span className="conn-recovery-summary__icon conn-recovery-summary__icon--success">
            <CheckCircleOutlined />
          </span>
          <div>
            <div className="conn-recovery-summary__value">{activeConnections.length}</div>
              <div className="conn-recovery-summary__label">Активных подключений доступно для восстановления</div>
          </div>
        </div>
        <div className="conn-recovery-summary__card">
          <span className="conn-recovery-summary__icon conn-recovery-summary__icon--muted">
            <HistoryOutlined />
          </span>
          <div>
            <div className="conn-recovery-summary__value">{inactiveConnections}</div>
            <div className="conn-recovery-summary__label">Неактивных, которые потребуют проверки</div>
          </div>
        </div>
      </div>

      <Alert
        className="conn-recovery-alert"
        type="info"
        showIcon
        message="Восстановление работает как отдельный оркестратор"
        description="Сценарий для «PostgreSQL жив, Kraken переустановлен»: синхронизация модемов, сброс устаревших связей, отправка пользователей и прокси, проверка результата."
      />

      <div className="conn-recovery-grid">
        <section className="conn-recovery-card">
          <div className="conn-recovery-card__header">
            <h2 className="conn-recovery-card__title">Предпроверка и запуск</h2>
            <button
              type="button"
              className={`conn-header__action-btn${loading || runsLoading ? ' conn-header__action-btn--loading' : ''}`}
              onClick={() => {
                loadConnections();
                loadRuns();
              }}
              disabled={loading || runsLoading}
              aria-label="Обновить данные восстановления"
              tabIndex={0}
            >
              <ReloadOutlined />
            </button>
          </div>

          {loading ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : (
            <>
              <div className="conn-recovery-form">
                <label className="conn-recovery-form__label" htmlFor="recovery-connections-select">
                  Подключения для восстановления
                </label>
                <Select
                  id="recovery-connections-select"
                  mode="multiple"
                  allowClear
                  value={selectedConnectionIds}
                  onChange={setSelectedConnectionIds}
                  options={connections.map((connection) => ({
                    value: connection.id,
                    label: `${connection.name}${connection.is_active ? '' : ' (неактивно)'}`,
                  }))}
                  placeholder="Оставьте пустым, чтобы взять все активные подключения"
                />
              </div>

              <div className="conn-recovery-actions">
                <button
                  type="button"
                  className={`conn-header__action-btn${preflightLoading ? ' conn-header__action-btn--loading' : ''}`}
                  style={{ width: 'auto', padding: '0 12px' }}
                  onClick={handleRunPreflight}
                  disabled={preflightLoading}
                >
                  Предпроверка
                </button>
                <button
                  type="button"
                  className={`conn-header__action-btn conn-header__action-btn--primary${startLoading ? ' conn-header__action-btn--loading' : ''}`}
                  style={{ width: 'auto', padding: '0 12px' }}
                  onClick={handleStartRecovery}
                  disabled={startLoading}
                >
                  Запустить восстановление
                </button>
              </div>

              {preflight ? (
                <div className="conn-recovery-block">
                  <div className="conn-recovery-block__header">
                    {preflight.ready_to_run ? renderStatusTag('completed') : renderStatusTag('failed')}
                    <span className="conn-recovery-block__meta">
                      id нет в списке Kraken: {preflight.counts?.stale_proxy_candidates || 0}, без kraken_proxy_id: {preflight.counts?.unsynced_proxies || 0}
                    </span>
                  </div>
                  {(preflight.blocking_issues || []).length > 0 ? (
                    <ul className="conn-recovery-checklist">
                      {preflight.blocking_issues.map((item, index) => (
                        <li key={`blocking-${index}`}>{item.message}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="conn-recovery-placeholder__text">Блокирующие проблемы не найдены, сценарий можно запускать.</p>
                  )}
                  {(preflight.warnings || []).length > 0 && (
                    <>
                      <p className="conn-recovery-placeholder__text">Предупреждения:</p>
                      <ul className="conn-recovery-checklist">
                        {preflight.warnings.map((item, index) => (
                          <li key={`warning-${index}`}>{item.message}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              ) : null}
            </>
          )}
        </section>

        <section className="conn-recovery-card">
          <div className="conn-recovery-card__header">
            <h2 className="conn-recovery-card__title">Активный запуск восстановления</h2>
          </div>
          {activeRun ? (
            <div className="conn-recovery-block">
              <div className="conn-recovery-block__header">
                <strong>Запуск #{activeRun.id}</strong>
                {renderStatusTag(activeRun.status)}
              </div>
              <p className="conn-recovery-placeholder__text">
                Текущая фаза: <strong>{RECOVERY_PHASE_LABELS[activeRun.phase] || activeRun.phase}</strong>
              </p>
              <p className="conn-recovery-placeholder__text">
                Старт: {formatDateTime(activeRun.started_at)} | Завершение: {formatDateTime(activeRun.finished_at)}
              </p>
              <div className="conn-recovery-stage-list">
                {stageRows.map((stage) => (
                  <div key={stage.key} className={getStageClassName(stage, activeRun)}>
                    <span>{stage.phase}</span>
                    <span>{getStageStatusLabel(stage.key, stage.hasReport, activeRun, stage.raw)}</span>
                  </div>
                ))}
              </div>
              {modemDefaultsResetSummary ? (
                <p className="conn-recovery-placeholder__text">
                  {modemDefaultsResetSummary}
                </p>
              ) : null}
              {orphanRebindSummary ? (
                <p className="conn-recovery-placeholder__text">
                  {orphanRebindSummary}
                </p>
              ) : null}
              {activeRunWarnings.length > 0 && (
                <>
                  <p className="conn-recovery-placeholder__text">Предупреждения:</p>
                  <ul className="conn-recovery-checklist">
                    {activeRunWarnings.map((item) => (
                      <li key={item.key}>{item.phase}: {item.message}</li>
                    ))}
                  </ul>
                </>
              )}
              {modemDefaultsResetErrors.length > 0 && (
                <>
                  <p className="conn-recovery-placeholder__text">Ошибки сброса модемов:</p>
                  <ul className="conn-recovery-checklist">
                    {modemDefaultsResetErrors.map((item) => (
                      <li key={item.key}>{item.message}</li>
                    ))}
                  </ul>
                </>
              )}
              {generalRunErrors.length > 0 && (
                <>
                  <p className="conn-recovery-placeholder__text">Ошибки:</p>
                  <ul className="conn-recovery-checklist">
                    {generalRunErrors.map((item) => (
                      <li key={item.key}>{item.message}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Сценарий восстановления еще не запускался"
            />
          )}
        </section>
      </div>

      <section className="conn-recovery-card">
        <div className="conn-recovery-card__header">
          <h2 className="conn-recovery-card__title">История восстановления</h2>
        </div>
        {runsLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Table
            rowKey="id"
            columns={runsColumns}
            dataSource={runs}
            pagination={false}
            locale={{ emptyText: 'История восстановления пока пуста' }}
            scroll={{ x: 960 }}
          />
        )}
      </section>
    </div>
  );
};

export default ConnectionsRecovery;
